// The takeoff → bid quantity snapshot, computed client-side. Quantities are
// the CLIENT's authority (editor-core derives them from geometry; the
// server never computes area) — "Sync from takeoff" runs the exact recipe
// the worksheet page uses (roomFigures over the master takeoff's wire
// rooms) and POSTs the figures, which Rails stores verbatim as
// bid_quantity_rows. Pure and transport-free: the Matrix page owns the
// fetch; money never appears here.

import { roomFigures, surfaceFigures } from "@/editor-core/addons"
import {
  type Subfloor,
  type WireRoom,
  type WireSurface,
  roomsFromServer,
  surfacesFromServer,
} from "@/editor-core/document"
import type { PlacedImage } from "@/editor-core/schedule"

export type SyncSource = {
  rooms: WireRoom[]
  surfaces: WireSurface[]
  calibrations: { sheet_id: number; label: string; ft_per_px: number | null }[]
  sheet_images: Record<number, PlacedImage[]>
  project_subfloor: Subfloor
}

export type SnapshotRow = {
  kind: "room" | "surface"
  client_id: string
  name: string
  product_type: string | null
  /** Rooms: net SF × multiplier, 2dp. Surfaces: the typed qty in its own
   *  unit. Null = unmeasured/untyped — the row stays VISIBLE with a blank
   *  price, never dropped. */
  qty: number | null
  multiplier: number
  unit: string
}

export type SnapshotResult = { rows: SnapshotRow[]; unmeasured: string[] }

const round2 = (n: number): number => Math.round(n * 100) / 100

/** Compute the snapshot rows for one plan's master takeoff. */
export function computeSnapshot(source: SyncSource): SnapshotResult {
  const rooms = roomsFromServer(source.rooms)
  const surfaces = surfacesFromServer(source.surfaces)
  const ftPerPxBySheet = new Map<number, number>(
    source.calibrations
      .filter((c) => c.ft_per_px != null)
      .map((c) => [c.sheet_id, c.ft_per_px as number])
  )

  const rows: SnapshotRow[] = []
  const unmeasured: string[] = []

  for (const room of rooms) {
    const figures = roomFigures(
      room,
      ftPerPxBySheet.get(room.sheetId) ?? null,
      source.sheet_images[room.sheetId] ?? [],
      source.project_subfloor
    )
    const qty = figures.netSf == null ? null : round2(figures.netSf * figures.multiplier)
    if (qty == null) unmeasured.push(room.name)
    rows.push({
      kind: "room",
      client_id: room.id,
      name: room.name,
      product_type: figures.productType,
      qty,
      multiplier: figures.multiplier,
      unit: "SF",
    })
  }

  for (const surface of surfaces) {
    const figures = surfaceFigures(surface, source.project_subfloor)
    rows.push({
      kind: "surface",
      client_id: surface.id,
      name: surface.name,
      product_type: surface.product?.product_type ?? null,
      qty: figures.manualQty == null ? null : round2(figures.manualQty),
      multiplier: 1,
      unit: figures.productUnit ?? "SF",
    })
  }

  return { rows, unmeasured }
}
